import { useState } from "react";
import { Heart } from "lucide-react";
import { useLang } from "../i18n";
import api, { formatApiError } from "../api";
import { track } from "../telemetry";
import Reveal from "../components/Reveal";
import Marquee from "../components/Marquee";

const AMOUNTS = [5, 20, 50, 100, 1000];

const Donate = () => {
  const { t } = useLang();
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(null);
  const [promo, setPromo] = useState("");
  const [disc, setDisc] = useState(null);
  const [promoMsg, setPromoMsg] = useState("");

  const applyPromo = async () => {
    setPromoMsg("");
    try {
      const { data } = await api.post("/promo/check", { code: promo });
      if (data.discount_percent > 0) {
        setDisc({ code: data.code, pct: data.discount_percent });
        navigator.clipboard?.writeText(data.code);
      } else {
        setPromoMsg(t("promo_only_gift"));
      }
    } catch (err) {
      setPromoMsg(formatApiError(err));
    }
  };

  const donate = async (amount) => {
    setBusy(amount); setError("");
    track("donate_click", `${amount}€${disc ? ` sconto ${disc.pct}%` : ""}`);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: `dona_${amount}`,
        origin_url: window.location.origin,
        promo_code: disc?.code || undefined,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      setError(formatApiError(err));
      setBusy(null);
    }
  };

  return (
    <main className="pt-16" data-testid="donate-page">
      <section className="max-w-5xl mx-auto px-5 sm:px-8 pt-20 pb-24">
        <Reveal className="text-center mb-14">
          <p className="font-mono2 text-xs tracking-[0.35em] uppercase text-ember mb-5">{t("donate_kicker")}</p>
          <h1 className="font-display font-extrabold text-3xl sm:text-5xl tracking-tight leading-tight mb-5" data-testid="donate-title">
            {t("donate_title")}
          </h1>
          <p className="text-mist max-w-2xl mx-auto text-sm sm:text-base">{t("donate_sub")}</p>
        </Reveal>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="donate-tiles">
          {AMOUNTS.map((a, i) => (
            <Reveal key={a} delay={i * 0.06}>
              <button
                data-testid={`donate-${a}`}
                onClick={() => donate(a)}
                disabled={busy !== null}
                className="group w-full rounded-2xl border border-white/10 bg-card p-7 text-center transition-all duration-500 hover:-translate-y-1.5 hover:border-gold/60 hover:bg-cardhover disabled:opacity-50"
              >
                <Heart className={`w-5 h-5 mx-auto mb-4 transition-colors ${a >= 100 ? "text-gold" : "text-ember"}`} />
                <p className="font-display font-bold text-sm text-gold mb-1" data-testid={`donate-tier-title-${a}`}>{t(`dona_tier_${a}_title`)}</p>
                <p className="font-display font-extrabold text-3xl mb-2" data-testid={`donate-price-${a}`}>
                  {disc ? (
                    <><span className="line-through text-mute text-xl mr-2">{a} €</span>{(a * (1 - disc.pct / 100)).toFixed(2).replace(".", ",")} €</>
                  ) : `${a} €`}
                </p>
                <p className="text-[11px] text-mist mb-3 leading-snug min-h-[3.5em]">{t(`dona_tier_${a}_desc`)}</p>
                <p className="font-mono2 text-[10px] tracking-[0.25em] uppercase text-mute group-hover:text-gold transition-colors">
                  {busy === a ? "…" : t("donate_btn")}
                </p>
              </button>
            </Reveal>
          ))}
        </div>
        {error && <p data-testid="donate-error" className="text-center text-sm text-red-400 mt-6">{error}</p>}
        <div className="max-w-sm mx-auto mt-10" data-testid="donate-promo-box">
          <p className="font-mono2 text-[10px] tracking-[0.25em] uppercase text-mute mb-3 text-center">{t("donate_promo_label")}</p>
          {disc ? (
            <p className="text-center">
              <button
                data-testid="donate-promo-chip"
                onClick={() => navigator.clipboard?.writeText(disc.code)}
                className="inline-flex items-center gap-2 rounded-full border border-dashed border-gold/60 bg-gold/10 px-4 py-1.5 text-xs font-mono2 tracking-widest text-gold hover:bg-gold hover:text-black transition-colors"
              >
                {disc.code} · −{disc.pct}% · {t("code_copy")}
              </button>
            </p>
          ) : (
            <div className="flex gap-2">
              <input
                data-testid="donate-promo-input"
                value={promo}
                onChange={(e) => setPromo(e.target.value)}
                placeholder={t("promo_placeholder")}
                className="flex-1 rounded-full border border-white/15 bg-black/40 px-5 py-3 text-sm placeholder:text-mute focus:border-gold focus:outline-none transition-colors"
              />
              <button
                data-testid="donate-promo-apply"
                onClick={applyPromo}
                className="rounded-full border border-gold/50 px-6 py-3 text-xs font-semibold uppercase tracking-wider text-gold hover:bg-gold hover:text-black transition-colors"
              >
                {t("donate_promo_btn")}
              </button>
            </div>
          )}
          {promoMsg && <p className="text-sm text-red-400 mt-3 text-center" data-testid="donate-promo-msg">{promoMsg}</p>}
        </div>
        <Reveal className="text-center mt-10">
          <p className="text-gold/90 text-xs mb-2" data-testid="donate-ship-note">{t("dona_ship_note")}</p>
          <p className="text-mute text-xs font-mono2 tracking-wider">{t("donate_note")}</p>
        </Reveal>
      </section>
      <Marquee text={t("marquee")} />
    </main>
  );
};

export default Donate;
